// File: typescript/apps/front-end/src/main/react/group/leave/groupLeave.ts
//
// The ONE leave in the product (chat completeness §F17, A14 / D16 / Q6). The group page's buttons and the
// room menu both call this module, so the two surfaces cannot drift apart on what leaving does.
//
// Kept deliberately small: `GroupButtons` is on the eager startup graph, so only the plain-member leave
// and the path resolver live here. The leader and owner paths (step down, transfer) are drawn by
// `GroupLeaderLeaveDialog`, which is code-split and loaded only when a leader presses Leave (rule 26).

import { toast } from "sonner";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import { trackGroupLeave, type AnalyticsSurface } from "@/main/analytics/analyticsEvents";
import { ClientFactory } from "@/main/common/ClientFactory";
import { emitGroupMemberRemoved } from "@/main/common/EventRegistry";
import { MetaHandler } from "@/main/common/MetaHandler";
import type { Localizer } from "@/main/react/Types";

/**
 * Which way out the viewer has.
 *
 * <ul>
 *   <li><b>owner</b>: pick a new owner, transfer, step down, leave. Step-down is NEVER offered to an owner
 *       (operator Q6): the server refuses it, because an owner must hand the group over first.</li>
 *   <li><b>leader</b>: an ADMINISTRATOR or MODERATOR steps down to member, then leaves.</li>
 *   <li><b>member</b>: leaves as before.</li>
 * </ul>
 *
 * `isLeader` covers a caller that knows the viewer leads but not at which level (the room view): that is
 * resolved to `leader` here, and the dialog reads the real level before it draws anything.
 */
export type GroupLeavePath = "member" | "leader" | "owner";

export function resolveGroupLeavePath(level: string | null | undefined, isLeader = false): GroupLeavePath {
    if (level === "OWNER") {
        return "owner";
    }
    if (level === "ADMINISTRATOR" || level === "MODERATOR" || isLeader) {
        return "leader";
    }
    return "member";
}

/**
 * Leave, then tell the rest of the page. Throws what the request throws, so each caller keeps its own
 * error presentation (the group page surfaces a 400/403/409's localized server reason; the room toasts).
 */
export async function completeGroupLeave(args: {
    groupPublicId: string;
    surface: AnalyticsSurface;
    localizer: Localizer;
}): Promise<void> {
    const { groupPublicId, surface, localizer } = args;
    await ClientFactory.getAjaxGroupRestControllerApi().leaveGroup(groupPublicId, surface);
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    if (loggedInUsername) {
        emitGroupMemberRemoved({ groupPublicId, username: loggedInUsername, source: "leave" });
    }
    trackGroupLeave(surface);
    toast.success(localizer("group.membership.left"));
}

export type GroupLeaderLeaveArgs = {
    groupPublicId: string;
    /** The viewer's level when the caller knows it; absent means "a leader, level unknown" (the room). */
    leadershipLevel?: string | null;
    privateAccess?: boolean;
    surface: AnalyticsSurface;
    localizer: Localizer;
    /** Called once the server confirmed the leave. */
    onLeft?: () => void;
};

/** Open the leader / owner way out. Code-split: loaded on the first press, never at startup. */
export function openGroupLeaderLeaveDialog(args: GroupLeaderLeaveArgs): Promise<void> {
    return importChunkWithRetryAndReload(
        () => import("@/main/react/group/leave/GroupLeaderLeaveDialog"),
        "group-leader-leave-dialog",
    ).then((module) => module.default(args));
}
